import ClearIcon from '@mui/icons-material/Clear';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import {
  Chip,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Select,
  Stack,
  styled,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import { useEditor, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useState, useRef, useMemo } from 'react';

import { WsListingLoader } from '../../components/WsListingLoader';
import { WsTypography } from '../../components/WsTypography';
import WsColors from '../../WsColors';

import { CustomTable } from './components/customTable';
import { CustomTableCell } from './components/customTableCell';
import { CustomTableHeader } from './components/customTableHeader';
import {
  BoldIcon,
  BulletPointsIcon,
  CenterAlignIcon,
  ColorHighlightIcon,
  ColorTextIcon,
  createEditorProps,
  CustomEditorContent,
  EditorToolbar,
  filterHref,
  HiddenInput,
  ImageUploadIcon,
  ItalicIcon,
  LeftAlignIcon,
  RedoIcon,
  RightAlignIcon,
  TableOptions,
  TableView,
  UndoIcon,
  // wrapAllTablesInFixedContainers,
} from './Helper';

const TemplateChip = styled(Chip)(() => ({
  borderRadius: 4,
  backgroundColor: WsColors.TheBlues100,
  border: '1px solid ' + WsColors.TheBlues500,
}));

const colorInputStyles: React.CSSProperties = {
  width: 32,
  height: 32,
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
};

export interface IEditorProps {
  editor: Editor | null;
  toolbar?: boolean;
  resizable?: boolean;
  templateName?: string;
  editorHeight?: number;
  handleChipClick?: () => void;
  handleChipDelete?: () => void;
  disable?: boolean;
  isLoaded?: boolean;
  onImageUpload?: (file: File) => Promise<string>;
}

export const useWsEditor = (
  content: string,
  onUpdateHtml?: (args: string | null) => void,
  onUpdateText?: (args: string | null) => void,
  onFocus?: () => void,
  onBlur?: () => void,
  onImageUpload?: (file: File) => Promise<string>
) => {
  const editorConfig = useMemo(
    () => ({
      extensions: [
        StarterKit,
        TextAlign.configure({
          types: ['heading', 'paragraph', 'table'],
        }),
        CustomTable,
        TableRow,
        CustomTableHeader,
        CustomTableCell,
        Link.configure({
          protocols: ['ftp', 'mailto'],
          validate: (href) => filterHref(href),
        }),
        Highlight.configure({ multicolor: true }),
        Color,
        TextStyle,
        Image.configure({
          allowBase64: true,
          inline: true,
        }),
      ],
      content,
      onUpdate: ({ editor }) => {
        // const rawHtml = editor.getHTML();
        // const patchedHtml = wrapAllTablesInFixedContainers(rawHtml);
        onUpdateHtml?.(editor.getHTML());
        onUpdateText?.(editor.getText());
      },
      onFocus: () => onFocus?.(),
      onBlur: () => onBlur?.(),
      editorProps: createEditorProps(onImageUpload),
    }),
    [content, onImageUpload, onUpdateHtml, onUpdateText, onFocus, onBlur]
  );

  const editor = useEditor(editorConfig);

  return editor;
};

const WsEditor = ({
  editor,
  toolbar = true,
  resizable = false,
  templateName,
  editorHeight = 400,
  handleChipClick,
  handleChipDelete,
  disable = false,
  isLoaded = true,
  onImageUpload,
}: IEditorProps) => {
  editor?.setEditable(!disable);
  const [tableOptionsAnchor, setTablePopupAnchor] = useState<HTMLElement | null>(null);
  const [currentTextColor, setCurrentTextColor] = useState<string>(WsColors.DefaultColor);
  const [currentHighliterColor, setCurrentHighliterColor] = useState<string>(WsColors.DefaultColor);

  const openTableOptions = Boolean(tableOptionsAnchor);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTableOptionsClick = (event: any) => {
    setTablePopupAnchor(event.currentTarget);
  };

  const closeTablePopup = () => {
    setTablePopupAnchor(null);
  };

  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && editor) {
      if (onImageUpload) {
        onImageUpload(file).then((url) => {
          editor.chain().focus().setImage({ src: url }).run();
        });
      } else {
        const reader = new FileReader();
        reader.onload = (readerEvent) => {
          const url = readerEvent.target?.result as string;
          editor.chain().focus().setImage({ src: url }).run();
        };
        reader.readAsDataURL(file);
      }
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const inputStyles = {
    width: '20px',
    marginLeft: '-1px',
    margginTop: '5px',
    marginBottom: '-5px',
  };

  const textFieldStyle = (
    display: string,
    marginLeft: string,
    marginBottom: string,
    marginTop: string,
    width: string,
    height: string,
    opacity: string
  ) => {
    return {
      display: display,
      marginLeft: marginLeft,
      marginBottom: marginBottom,
      marginTop: marginTop,
      width: width,
      height: height,
      opacity: opacity,
    };
  };

  return (
    <>
      {toolbar && (
        <EditorToolbar>
          {!disable && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={2}>
                <UndoIcon onClick={() => editor?.chain().focus().undo().run()} />
                <RedoIcon onClick={() => editor?.chain().focus().redo().run()} />
                <Divider orientation="vertical" flexItem />
                <BoldIcon onClick={() => editor?.chain().focus().toggleBold().run()} />
                <ItalicIcon onClick={() => editor?.chain().focus().toggleItalic().run()} />
                <BulletPointsIcon
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}
                />
                <Divider orientation="vertical" flexItem />
                <LeftAlignIcon onClick={() => editor?.chain().focus().setTextAlign('left').run()} />
                <CenterAlignIcon
                  onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                />
                <RightAlignIcon
                  onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                />
                <Divider orientation="vertical" flexItem />
                <TableView onClick={() => editor?.chain().focus().insertTable().run()} />

                <TableOptions onMouseEnter={handleTableOptionsClick} />
                <Menu
                  anchorEl={tableOptionsAnchor}
                  id="Table Options"
                  open={openTableOptions}
                  onClose={closeTablePopup}
                  onClick={closeTablePopup}
                  onMouseLeave={closeTablePopup}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem onClick={() => editor?.chain().focus().addColumnBefore().run()}>
                    Add Column Before
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().addColumnAfter().run()}>
                    Add Column After
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().deleteColumn().run()}>
                    Delete Column
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().addRowBefore().run()}>
                    Add Row Before
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().addRowAfter().run()}>
                    Add Row After
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().deleteRow().run()}>
                    Delete Row
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().mergeCells().run()}>
                    Merge Cells
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().splitCell().run()}>
                    Split Current Cell
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().fixTables().run()}>
                    Fix Table
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().deleteTable().run()}>
                    Delete Table
                  </MenuItem>
                </Menu>
                <ImageUploadIcon onClick={handleImageUploadClick} />
                <HiddenInput
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <Select
                  defaultValue={0}
                  size="small"
                  onChange={(ev) =>
                    ev.target.value !== 0
                      ? editor
                          ?.chain()
                          .focus()
                          .toggleHeading({ level: ev.target.value as 1 | 2 | 3 | 4 | 5 | 6 })
                          .run()
                      : editor?.chain().focus().setParagraph().run()
                  }
                >
                  <MenuItem value={0}>Paragraph</MenuItem>
                  <MenuItem value={1}>Heading 1</MenuItem>
                  <MenuItem value={2}>Heading 2</MenuItem>
                  <MenuItem value={3}>Heading 3</MenuItem>
                  <MenuItem value={4}>Heading 4</MenuItem>
                  <MenuItem value={5}>Heading 5</MenuItem>
                  <MenuItem value={6}>Heading 6</MenuItem>
                </Select>
                <Divider orientation="vertical" flexItem />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Text color">
                    <label htmlFor="colorPicker">
                      <ColorTextIcon style={inputStyles} />
                    </label>
                  </Tooltip>

                  <input
                    type="color"
                    id="colorPicker"
                    style={colorInputStyles}
                    value={currentTextColor}
                    onChange={(event) => {
                      const color = (event.target as HTMLInputElement).value;
                      setCurrentTextColor(color);
                      editor && editor.chain().focus().setColor(color).run();
                    }}
                  />

                  <Tooltip title="Clear text color">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCurrentTextColor(WsColors.DefaultColor);
                        editor?.chain().focus().unsetColor().run();
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Highlight color">
                    <label htmlFor="highlightColor">
                      <ColorHighlightIcon style={inputStyles} />
                    </label>
                  </Tooltip>

                  <input
                    type="color"
                    id="highlightColor"
                    style={colorInputStyles}
                    value={currentHighliterColor}
                    onChange={(event) => {
                      const color = (event.target as HTMLInputElement).value;
                      setCurrentHighliterColor(color);
                      editor?.chain().focus().toggleHighlight({ color }).run();
                    }}
                  />

                  <Tooltip title="Clear highlight color">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCurrentHighliterColor(WsColors.DefaultColor);
                        editor?.chain().focus().unsetHighlight().run();
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <div>
                  <label htmlFor="cellFillColor" title="Cell background color">
                    <i>
                      <FormatColorFillIcon style={inputStyles} />
                    </i>
                  </label>
                  <input
                    type="color"
                    id="cellFillColor"
                    style={textFieldStyle('block', '10px', '10px', '-10px', '0px', '0px', '0')}
                    onInput={(event) => {
                      const color = (event.target as HTMLInputElement).value;
                      editor
                        ?.chain()
                        .focus()
                        .updateAttributes('tableCell', {
                          backgroundColor: color,
                        })
                        .run();
                      editor
                        ?.chain()
                        .focus()
                        .updateAttributes('tableHeader', {
                          backgroundColor: color,
                        })
                        .run();
                    }}
                  />
                </div>
              </Stack>
              {templateName && (
                <TemplateChip
                  label={templateName.substring(0, 50) + '...'}
                  clickable
                  onClick={handleChipClick}
                  onDelete={handleChipDelete}
                />
              )}
            </Stack>
          )}
        </EditorToolbar>
      )}
      {!isLoaded ? (
        <WsListingLoader>
          <WsTypography bold>Loading Content...</WsTypography>
        </WsListingLoader>
      ) : resizable ? (
        <Grid
          item
          xs={12}
          sx={{ height: editorHeight, width: '100%', overflow: 'auto' }}
          onClick={() => editor?.chain().focus()}
          onKeyDown={() => editor?.chain().focus()}
        >
          <CustomEditorContent editor={editor} disabled={disable} />
        </Grid>
      ) : (
        <CustomEditorContent editor={editor} disabled={disable} />
      )}
    </>
  );
};

export default WsEditor;
